import { realtimeDatabase } from '@util/firebase';
import { ref, set, onValue, onDisconnect } from 'firebase/database';
import { useEffect, useState } from 'react';
import { timeAgo } from '..';

interface OnlineStatus {
  state: 'online' | 'offline';
  last_changed: number;
}

export const useOnlineStatus = (uid?: string) => {
  // set online status
  function setOnlineStatus(uid: string) {
    const statusRef = ref(realtimeDatabase, `/status/${uid}`);
    const last_changed = new Date().getTime(); // todo: change this to use firebase server timestamp
    set(statusRef, {
      state: 'online',
      last_changed,
    });
    const infoConnectedRef = ref(realtimeDatabase, '.info/connected');
    onValue(infoConnectedRef, (snapshot) => {
      const isOnline = snapshot.val();
      if (isOnline) {
        onDisconnect(statusRef).set({
          state: 'offline',
          last_changed,
        });
      }
    });
  }

  const [status, setStatus] = useState<OnlineStatus | null>(null);

  useEffect(() => {
    // get online status
    function getOnlineStatus() {
      const statusRef = ref(realtimeDatabase, '/status/' + uid);
      return onValue(statusRef, (snapshot) => {
        const data = snapshot.val();
        setStatus(data);
      });
    }

    if (uid) {
      const unsubscribe = getOnlineStatus();
      return () => {
        unsubscribe();
      };
    }
  }, [uid]);

  const StatusLabel = () => {
    if (!status?.last_changed) return null;

    if (status?.state === 'online') {
      return (
        <div className="flex items-center gap-[0.6rem]">
          <i className="h-[0.6rem] w-[0.6rem] rounded-full bg-brand-green"></i>
          <span className="text-[1.3rem] font-normal">Online</span>
        </div>
      );
    }

    // Friendly "last seen" labels based on how long ago
    const msAgo = Date.now() - status.last_changed;
    const days = msAgo / (1000 * 60 * 60 * 24);

    // Hidden if more than 6 months ago
    if (days > 180) return null;

    let label: string;
    if (days > 60) label = 'Last seen recently';
    else if (days > 30) label = 'Last seen last month';
    else if (days > 14) label = 'Last seen this month';
    else if (days > 7) label = 'Last seen last week';
    else if (days > 2) label = 'Last seen this week';
    else if (days > 1) label = 'Last seen yesterday';
    else if (days >= 1) label = 'Last seen today';
    else label = `Last seen ${timeAgo(status.last_changed)}`;

    return (
      <div className="flex items-center gap-[0.6rem]">
        <i className="h-[0.6rem] w-[0.6rem] rounded-full bg-brand-gray"></i>
        <span className="text-[1.3rem] font-normal">{label}</span>
      </div>
    );
  };

  return { setOnlineStatus, status, StatusLabel };
};
